import { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { BfiDataGrid, Column } from '../../common/SharedComponents';
import {
    ExperienceProjectTypeOptions,
    ExperienceProjectType,
    StructureTypeOptions,
    GridSettingType,
    LoanStatusType,
} from '@apply/constants';
import { ExperienceApi } from '@apply/api';
import { applicationInsights } from '../../ApplicationInsights';
import { formatCurrency } from '../../common/SharedFunctions';
import store, { experienceActions } from '@apply/adminStore';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEye } from '@fortawesome/free-regular-svg-icons';
import { onCityCellRender, onStateCellRender, onBFIExperienceActionCellRender } from '../common/ExperienceFunctions';
import { useLocation } from 'react-router';

const BfiRepaidTable = ({ principalId, gridStore }) => {
    const location = useLocation();
    const searchState = useSelector((state) => state.searchState);
    const { dispatch } = store;
    const experienceApi = new ExperienceApi.ExperienceApi(applicationInsights);
    const [dataStore, set_dataStore] = useState(gridStore?.store);

    const dataFunction = (criteria) => {
        return experienceApi.getExperiencesBfiRepaidGrid(principalId, criteria);
    };

    useEffect(() => {
        if (gridStore) {
            gridStore.setOnLoad('id', dataFunction, searchState?.searchText?.experience);
            set_dataStore(gridStore.store);
        }
    }, [gridStore]);

    const onRenderDate = (e) => {
        if (!e.value) {
            return null;
        }
        return new Date(e.value).toLocaleDateString('en-us');
    };

    const onRenderCurrency = (e) => {
        if (!e.value) {
            return null;
        }
        return formatCurrency(e.value, 0);
    };

    const onRenderCommitted = (e) => {
        let committedAmt = null;
        if (e.data.isPortfolio) {
            committedAmt = e.data.commitment + e.data.originationFee;
        } else {
            committedAmt = e.data.promissoryNote;
        }

        if (!committedAmt) {
            return null;
        }
        return formatCurrency(committedAmt, 0);
    };

    const onProjectTypeRender = (e) => {
        const option = ExperienceProjectTypeOptions.find((a) => a.id === e.value);
        if (!option) {
            return null;
        }
        if (option.id === ExperienceProjectType.FixFlipRehab) {
            if (e.data.isMajorRehab) {
                return `${option.adminName} (Major)`;
            } else {
                return option.adminName;
            }
        } else {
            return option.adminName;
        }
    };

    const onStructureTypeRender = (e) => {
        const option = StructureTypeOptions.find((a) => a.id === e.value);
        if (!option) {
            return null;
        } else {
            return option.shortName;
        }
    };

    const onRoleRender = (e) => {
        if (e.value) {
            return '20% Plus Owner';
        } else {
            return 'Not 20% Owner';
        }
    };

    const viewDetails = (event, id) => {
        event?.preventDefault();
        event?.stopPropagation();

        dispatch(
            experienceActions.setBfiDetailModal({
                bfiDetailModal: { isOpen: true, id },
            })
        );
    };

    if (!principalId || !dataStore) {
        return null;
    }

    return (
        <BfiDataGrid
            gridType={GridSettingType.ExperiencesBfiRepaidTable}
            dataStore={dataStore}
            applicationInsights={applicationInsights}
            noDataText='No BFI-Repaid Experience found for this Principal'
            onRowDblClick={(e) => viewDetails(null, e.data.id)}
        >
            <Column
                dataField='projectTypeId'
                caption='Project Type'
                alignment='left'
                cellRender={onProjectTypeRender}
                width={200}
            />
            <Column
                dataField='structureTypeId'
                caption='Structure Type'
                alignment='left'
                cellRender={onStructureTypeRender}
                width={150}
            />
            <Column
                dataField='city'
                caption='City'
                width={150}
                cellRender={onCityCellRender}
            />
            <Column
                dataField='state'
                caption='State'
                width={50}
                cellRender={onStateCellRender}
            />
            <Column
                dataField='bfiNumber'
                caption='BFI #'
                width={125}
            />
            <Column
                dataField='lastPayDate'
                caption='Date Repaid'
                width={100}
                cellRender={onRenderDate}
            />
            <Column
                dataField='monthsOutstanding'
                caption='Months'
                width={60}
                alignment='left'
            />
            <Column
                dataField='isTwentyPercentPlusOwner'
                caption='Role'
                alignment='left'
                cellRender={onRoleRender}
            />
            <Column
                caption='Committed'
                width={100}
                alignment='left'
                cellRender={onRenderCommitted}
            />
            <Column
                dataField='repaidAmount'
                caption='Repaid'
                width={100}
                alignment='left'
                cellRender={onRenderCurrency}
            />
            <Column
                dataField='appraisedValue'
                caption='Value'
                width={100}
                alignment='left'
                cellRender={onRenderCurrency}
            />
            <Column
                dataField='ltv'
                caption='LTV'
                width={100}
                format={{ type: 'percent', precision: 1 }}
                alignment='left'
            />
            <Column
                dataField='salePrice'
                caption='Sold'
                width={100}
                cellRender={onRenderCurrency}
                alignment='left'
            />
            <Column
                dataField='unitCount'
                caption='Units'
                width={60}
                alignment='left'
            />
            <Column
                dataField='credits'
                caption='Crdts'
                alignment='left'
                width={50}
            />
            <Column
                alignment='center'
                cellRender={(e) => onBFIExperienceActionCellRender(e, location)}
                width={50}
                allowReordering={false}
                allowSorting={false}
                allowResizing={false}
                type='actions'
                fixed={true}
                fixedPosition={'right'}
            />
        </BfiDataGrid>
    );
};

export default BfiRepaidTable;
